import {
  Box,
  Chip,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import SecurityIcon from "@mui/icons-material/SecurityOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PendingIcon from "@mui/icons-material/PendingOutlined";
import WarningIcon from "@mui/icons-material/WarningAmberOutlined";
import { useEffect, useState } from "react";
import { useTranslation } from "../../i18n";
import { glass } from "../../theme/glassTokens";
import api from "../../api/api";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type AgreementSummary = {
  id: string;
  document_title: string;
  status: string;
  signed_by_name: string | null;
  signed_at: string | null;
  created_at: string;
};

type ConsentRecord = {
  id: string;
  patient: number;
  document_title: string;
  status: string;
  language: string;
  signed_at: string | null;
  withdrawn_at: string | null;
  created_at: string;
};

type ConsentStats = {
  total: number;
  signed: number;
  pending: number;
  withdrawn: number;
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function ComplianceDashboard() {
  const theme = useTheme();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [agreements, setAgreements] = useState<AgreementSummary[]>([]);
  const [consentRecords, setConsentRecords] = useState<ConsentRecord[]>([]);
  const [consentStats, setConsentStats] = useState<ConsentStats>({ total: 0, signed: 0, pending: 0, withdrawn: 0 });

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [agResp, crResp] = await Promise.allSettled([
          api.get<AgreementSummary[]>("/billing/legal/institution/agreements/"),
          api.get<ConsentRecord[]>("/billing/legal/consent/records/"),
        ]);

        if (!mounted) return;

        if (agResp.status === "fulfilled") {
          setAgreements(agResp.value.data);
        }

        if (crResp.status === "fulfilled") {
          const records = crResp.value.data;
          setConsentRecords(records);
          setConsentStats({
            total: records.length,
            signed: records.filter((r) => r.status === "signed").length,
            pending: records.filter((r) => r.status === "pending").length,
            withdrawn: records.filter((r) => r.status === "withdrawn").length,
          });
        }
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const sectionSx = {
    p: 2.5,
    borderRadius: 2,
    border: `1px solid ${alpha(theme.palette.secondary.main, 0.18)}`,
    background: glass.panel,
    backdropFilter: "blur(10px)",
  };

  const statusIcon = (status: string) => {
    switch (status) {
      case "signed":
        return <CheckCircleIcon sx={{ fontSize: 16, color: theme.palette.success.light }} />;
      case "pending":
      case "draft":
      case "pending_review":
        return <PendingIcon sx={{ fontSize: 16, color: theme.palette.warning.main }} />;
      case "withdrawn":
      case "expired":
      case "revoked":
        return <WarningIcon sx={{ fontSize: 16, color: theme.palette.error.light }} />;
      default:
        return <PendingIcon sx={{ fontSize: 16 }} />;
    }
  };

  const statusColor = (status: string) => {
    switch (status) {
      case "signed":
        return { color: theme.palette.success.light, borderColor: alpha(theme.palette.success.light, 0.5) };
      case "pending":
      case "draft":
      case "pending_review":
        return { color: theme.palette.warning.main, borderColor: alpha(theme.palette.warning.main, 0.5) };
      case "withdrawn":
      case "expired":
      case "revoked":
        return { color: theme.palette.error.light, borderColor: alpha(theme.palette.error.light, 0.5) };
      default:
        return { color: glass.textSecondary, borderColor: glass.headerDivider };
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Stack spacing={2.5}>
      {/* Header */}
      <Box sx={sectionSx}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <SecurityIcon sx={{ fontSize: 22, color: theme.palette.secondary.main }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
            {t("legal.compliance.title", "Compliance Overview")}
          </Typography>
        </Box>
        <Typography variant="body2" color={glass.textSecondary}>
          {t(
            "legal.compliance.description",
            "Overview of all legal agreements and patient consent records for your organization.",
          )}
        </Typography>
      </Box>

      {/* Summary Cards */}
      <Stack direction="row" spacing={2} flexWrap="wrap">
        <Box sx={{ ...sectionSx, flex: 1, minWidth: 140, textAlign: "center" }}>
          <Typography variant="h4" sx={{ fontWeight: 800, color: glass.accent }}>
            {agreements.length}
          </Typography>
          <Typography variant="caption" sx={{ fontWeight: 700 }}>
            {t("legal.compliance.total_agreements", "Institutional Agreements")}
          </Typography>
        </Box>
        <Box sx={{ ...sectionSx, flex: 1, minWidth: 140, textAlign: "center" }}>
          <Typography variant="h4" sx={{ fontWeight: 800, color: theme.palette.success.light }}>
            {agreements.filter((a) => a.status === "signed").length}
          </Typography>
          <Typography variant="caption" sx={{ fontWeight: 700 }}>
            {t("legal.compliance.signed_agreements", "Signed")}
          </Typography>
        </Box>
        <Box sx={{ ...sectionSx, flex: 1, minWidth: 140, textAlign: "center" }}>
          <Typography variant="h4" sx={{ fontWeight: 800, color: glass.accentAlt }}>
            {consentStats.total}
          </Typography>
          <Typography variant="caption" sx={{ fontWeight: 700 }}>
            {t("legal.compliance.total_consents", "Patient Consents")}
          </Typography>
        </Box>
        <Box sx={{ ...sectionSx, flex: 1, minWidth: 140, textAlign: "center" }}>
          <Typography variant="h4" sx={{ fontWeight: 800, color: theme.palette.warning.main }}>
            {agreements.filter((a) => a.status !== "signed").length + consentStats.pending}
          </Typography>
          <Typography variant="caption" sx={{ fontWeight: 700 }}>
            {t("legal.compliance.pending_actions", "Pending Actions")}
          </Typography>
        </Box>
      </Stack>

      {/* Agreements Table */}
      {agreements.length > 0 && (
        <Box sx={sectionSx}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1.5 }}>
            {t("legal.compliance.agreements_table", "Institutional Agreements")}
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem" }}>
                    {t("legal.compliance.document", "Document")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem" }}>
                    {t("legal.compliance.status", "Status")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem" }}>
                    {t("legal.compliance.signed_by", "Signed By")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem" }}>
                    {t("legal.compliance.date", "Date")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {agreements.map((ag) => (
                  <TableRow key={ag.id}>
                    <TableCell sx={{ fontSize: "0.8rem" }}>{ag.document_title}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        icon={statusIcon(ag.status)}
                        label={ag.status.replace("_", " ").toUpperCase()}
                        variant="outlined"
                        sx={{
                          height: 22,
                          fontSize: "0.68rem",
                          fontWeight: 700,
                          ...statusColor(ag.status),
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontSize: "0.8rem" }}>{ag.signed_by_name || "-"}</TableCell>
                    <TableCell sx={{ fontSize: "0.8rem" }}>
                      {ag.signed_at
                        ? new Date(ag.signed_at).toLocaleDateString()
                        : new Date(ag.created_at).toLocaleDateString()}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Patient Consent Summary */}
      {consentStats.total > 0 && (
        <Box sx={sectionSx}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1.5 }}>
            {t("legal.compliance.consent_summary", "Patient Consent Summary")}
          </Typography>
          <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
            <Chip
              icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
              label={`${consentStats.signed} ${t("legal.compliance.consent_signed", "Signed")}`}
              variant="outlined"
              sx={{ fontWeight: 700, ...statusColor("signed") }}
            />
            <Chip
              icon={<PendingIcon sx={{ fontSize: 14 }} />}
              label={`${consentStats.pending} ${t("legal.compliance.consent_pending", "Pending")}`}
              variant="outlined"
              sx={{ fontWeight: 700, ...statusColor("pending") }}
            />
            <Chip
              icon={<WarningIcon sx={{ fontSize: 14 }} />}
              label={`${consentStats.withdrawn} ${t("legal.compliance.consent_withdrawn", "Withdrawn")}`}
              variant="outlined"
              sx={{ fontWeight: 700, ...statusColor("withdrawn") }}
            />
          </Stack>

          {consentRecords.length > 0 && consentRecords.length <= 50 && (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem" }}>
                      {t("legal.compliance.patient_id", "Patient")}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem" }}>
                      {t("legal.compliance.consent_doc", "Document")}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem" }}>
                      {t("legal.compliance.language", "Language")}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem" }}>
                      {t("legal.compliance.status", "Status")}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem" }}>
                      {t("legal.compliance.date", "Date")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {consentRecords.slice(0, 20).map((cr) => (
                    <TableRow key={cr.id}>
                      <TableCell sx={{ fontSize: "0.8rem" }}>#{cr.patient}</TableCell>
                      <TableCell sx={{ fontSize: "0.8rem" }}>{cr.document_title}</TableCell>
                      <TableCell>
                        <Chip size="small" label={cr.language.toUpperCase()} variant="outlined" sx={{ height: 20, fontSize: "0.68rem", fontWeight: 700 }} />
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          icon={statusIcon(cr.status)}
                          label={cr.status.toUpperCase()}
                          variant="outlined"
                          sx={{ height: 22, fontSize: "0.68rem", fontWeight: 700, ...statusColor(cr.status) }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.8rem" }}>
                        {cr.signed_at ? new Date(cr.signed_at).toLocaleDateString() : new Date(cr.created_at).toLocaleDateString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      {/* Empty state */}
      {agreements.length === 0 && consentStats.total === 0 && (
        <Box sx={{ ...sectionSx, textAlign: "center", py: 4 }}>
          <SecurityIcon sx={{ fontSize: 48, color: glass.textAbsent, mb: 1 }} />
          <Typography variant="body2" color={glass.textSecondary}>
            {t("legal.compliance.empty", "No agreements or consent records yet. Documents will appear here as they are signed.")}
          </Typography>
        </Box>
      )}
    </Stack>
  );
}
